import { useLayoutEffect, useRef } from 'react';

import { useHostProps } from '../hooks/useHostProps.js';
import { useStartChatRequest } from '../hooks/useStartChatRequest.js';
import { emitToHost } from '../lib/bridge.js';
import { track } from '../lib/analytics.js';
import { startReplay } from '../lib/replay.js';
import { useSession } from '../session/SessionProvider.js';
import { ChatWidget } from '../widget/ChatWidget.js';

/**
 * Floating widget surface, served at /widget inside the launcher iframe injected
 * by widget.js. Renders the bespoke ChatWidget on fixtures (UI-only) — bare, so
 * the host page shows through the transparent iframe.
 *
 * Emits `open`/`close` to the loader so it can resize the iframe (launcher ⇄
 * panel) — without this the panel opens clipped inside the tiny launcher iframe.
 * The rest of the bridge (Stream connection, the auth handoff/session, unread
 * events) is restored by the re-wiring epic; the previous stream-chat-react
 * implementation is preserved in git history.
 *
 * Expanding is handed to the HOST, not routed here. This surface lives in a
 * ~424px iframe, so routing to /chat inside it just loads the full workspace
 * into the launcher frame — which renders as the mobile layout, cropped. Only
 * the host can leave the host page, so it gets an `expand` event and does a real
 * document navigation (widget.js handles it; see its `chatPageUrl` option).
 */
export function WidgetView() {
  // Skip the mount-time `onOpenChange(false)`: `widget_close` should only fire
  // after a real open, never for a widget that was never opened.
  const opened = useRef(false);

  // Apply host theme/context to the whole widget surface (launcher included),
  // independent of whether the panel is open.
  useHostProps();

  // Drop the page background so the host shows through the transparent iframe
  // (globals.css). Layout effect so no grey frame paints before the launcher.
  useLayoutEffect(() => {
    document.documentElement.dataset.surface = 'widget';
    return () => {
      delete document.documentElement.dataset.surface;
    };
  }, []);

  // The host's Start / View / Reply buttons, and its headless auto-send. The
  // hook answers the headless path itself; only the two that become UI reach
  // the widget below.
  const { request, clear } = useStartChatRequest();
  const { analyticsId } = useSession();

  return (
    <ChatWidget
      request={request}
      onRequestHandled={clear}
      onExpand={(req) => emitToHost('expand', req)}
      onOpenChange={(open, method) => {
        // `method` mirrors the stream-chat web component's widget-open/close, so a
        // host that built a funnel there (launcher tap vs. the reply nudge) reuses
        // it. Omitted, not guessed, for the mount-time closed report.
        emitToHost(open ? 'open' : 'close', method ? { method } : {});
        if (open) {
          opened.current = true;
          track('widget_open', method ? { method } : {});
          // On the panel opening, never on the launcher mounting — the launcher
          // rides every employer page on the site and almost none of them
          // become a chat. See `lib/replay.ts`.
          if (analyticsId) void startReplay(analyticsId);
        } else if (opened.current) {
          track('widget_close', method ? { method } : {});
        }
      }}
      authGate
    />
  );
}
